import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import { githubLight } from "@uiw/codemirror-theme-github";
import CodeMirror from "@uiw/react-codemirror";
import { ExternalLink, GripVertical, Trash2 } from "lucide-react";
import { useState } from "react";
import { Controller, type Control, type FieldError } from "react-hook-form";
import { showToast } from "@utils/helper/index.helper";

type Props = {
  id: string;
  index: number;
  control: Control<any>;
  name: string;
  error?: {
    name?: FieldError;
    content?: FieldError;
  };
  onRemove: () => void;
};

const SchemaBlock = ({ id, index, control, name, error, onRemove }: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [touched, setTouched] = useState(false);

  const fieldName = `${name}.${index}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-gray-200 rounded-md bg-white w-full min-w-0 overflow-hidden"
    >
      {/* Header bar: drag handle + name input + actions */}
      <div className="flex items-center gap-2 p-3 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical size={18} />
        </button>

        <div className="flex-1">
          <Controller
            control={control}
            name={`${fieldName}.name`}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                placeholder="Schema name (optional, e.g. Course, FAQPage)"
                className="h-8 text-sm"
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name={`${fieldName}.content`}
          render={({ field }) => (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={async () => {
                const value = (field.value ?? "").trim();
                if (!value) {
                  showToast({
                    title: "Empty schema",
                    description: "Add some JSON before previewing.",
                    type: "warning",
                  });
                  return;
                }
                try {
                  JSON.parse(value);
                } catch {
                  showToast({
                    title: "Invalid JSON",
                    description: "Fix syntax errors before previewing.",
                    type: "error",
                  });
                  return;
                }
                try {
                  await navigator.clipboard.writeText(value);
                  showToast({
                    title: "JSON copied",
                    description:
                      "Paste it into the 'Code' tab on Google Rich Results Test.",
                    type: "success",
                  });
                } catch {
                  // clipboard may fail in some contexts; still open the tool
                }
                window.open(
                  "https://search.google.com/test/rich-results",
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <ExternalLink size={14} />
              Preview
            </Button>
          )}
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          aria-label="Delete schema"
        >
          <Trash2 size={16} />
        </Button>
      </div>

      {/* Editor */}
      <Controller
        control={control}
        name={`${fieldName}.content`}
        render={({ field }) => (
          <div className="w-full min-w-0 overflow-hidden">
            <CodeMirror
              value={field.value ?? ""}
              minHeight="160px"
              maxHeight="400px"
              width="100%"
              extensions={[json(), EditorView.lineWrapping]}
              theme={githubLight}
              basicSetup={{
                lineNumbers: true,
                autocompletion: true,
                bracketMatching: true,
                foldGutter: true,
              }}
              onChange={(val) => field.onChange(val)}
              onBlur={() => {
                setTouched(true);
                field.onBlur();
              }}
            />
            {touched && error?.content?.message && (
              <p className="text-xs text-red-600 px-3 py-2 border-t border-red-100 bg-red-50">
                {error.content.message}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default SchemaBlock;
